"use client";

import { useEffect, useRef, useState } from "react";

type KineticHeadlineProps = {
  lines: string[];
  as?: "h1" | "h2" | "h3";
  className?: string;
  variant?: "slide-up" | "blur-focus" | "word-flip";
  id?: string;
};

export function KineticHeadline({
  lines,
  as: Tag = "h2",
  className = "",
  variant = "slide-up",
  id,
}: KineticHeadlineProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // gsap + ScrollTrigger are loaded dynamically here, not imported at module scope — every page
  // using KineticHeadline (nearly every page) otherwise paid for gsap's bundle before first paint
  // even though this reveal is purely cosmetic and can start a beat after initial render.
  useEffect(() => {
    if (variant === "word-flip") return; // handled entirely by WordFlipLines below
    const el = containerRef.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const lineEls = el.querySelectorAll<HTMLElement>("[data-kinetic-line]");

    let cancelled = false;
    let ctx: ReturnType<typeof import("gsap").default.context> | undefined;

    Promise.all([import("gsap"), import("gsap/ScrollTrigger")]).then(
      ([{ default: gsap }, { ScrollTrigger }]) => {
        if (cancelled) return;
        gsap.registerPlugin(ScrollTrigger);

        if (prefersReducedMotion) {
          gsap.set(lineEls, { opacity: 1, y: 0, filter: "blur(0px)" });
          return;
        }

        const fromVars =
          variant === "blur-focus"
            ? { opacity: 0, filter: "blur(12px)", y: 0 }
            : { opacity: 0, y: 40, filter: "blur(0px)" };

        ctx = gsap.context(() => {
          gsap.fromTo(
            lineEls,
            fromVars,
            {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              duration: 0.9,
              ease: "power3.out",
              stagger: 0.12,
              scrollTrigger: {
                trigger: el,
                start: "top 85%",
                toggleActions: "play none none reverse",
              },
            }
          );
        }, el);
      }
    );

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, [variant]);

  if (variant === "word-flip") {
    return (
      <Tag id={id} className={className}>
        <WordFlipLines lines={lines} />
      </Tag>
    );
  }

  return (
    <Tag ref={containerRef as never} id={id} className={className}>
      {lines.map((line, i) => (
        <span key={i} data-kinetic-line className="block will-change-transform">
          {line}
        </span>
      ))}
    </Tag>
  );
}

const FLIP_DURATION_MS = 650;
const FLIP_MIN_PAUSE_MS = 1800;
const FLIP_MAX_PAUSE_MS = 4200;

function WordFlipLines({ lines }: { lines: string[] }) {
  const wordsPerLine = lines.map((line) => line.split(" "));
  const totalWords = wordsPerLine.reduce((sum, words) => sum + words.length, 0);
  const wordRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const rafRef = useRef<number | null>(null);
  const [reducedMotion, setReducedMotion] = useState(true);

  useEffect(() => {
    setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;

    // Each word tracks its own turn count + next-flip timestamp independently — no shared start
    // time, so words never move in lockstep across the headline.
    const state = Array.from({ length: totalWords }, () => ({
      turns: 0,
      flipStartedAt: performance.now() + FLIP_MIN_PAUSE_MS + Math.random() * (FLIP_MAX_PAUSE_MS - FLIP_MIN_PAUSE_MS),
      flipping: false,
    }));

    let rafId: number | null = null;
    let sleepTimer: ReturnType<typeof setTimeout> | null = null;

    function setWordRotation(i: number, rotation: number) {
      const el = wordRefs.current[i];
      if (el) el.style.transform = `rotateX(${rotation}deg)`;
    }

    function anyFlipping(now: number) {
      return state.some((s) => s.flipping || now >= s.flipStartedAt);
    }

    function scheduleSleep() {
      const now = performance.now();
      const next = Math.min(...state.map((s) => s.flipStartedAt));
      const delay = Math.max(16, next - now);
      sleepTimer = setTimeout(() => {
        if (anyFlipping(performance.now())) {
          rafRef.current = rafId = requestAnimationFrame(tick);
        } else {
          scheduleSleep();
        }
      }, delay);
    }

    function tick(now: number) {
      state.forEach((s, i) => {
        if (!s.flipping && now < s.flipStartedAt) {
          setWordRotation(i, -180 * s.turns);
          return;
        }
        if (!s.flipping) {
          s.flipping = true;
          s.flipStartedAt = now;
        }
        const local = Math.min(1, (now - s.flipStartedAt) / FLIP_DURATION_MS);
        const eased = 1 - Math.pow(1 - local, 3); // ease-out cubic
        const rotation = -180 * (s.turns + eased);
        if (local >= 1) {
          s.flipping = false;
          s.turns += 1;
          s.flipStartedAt = now + FLIP_MIN_PAUSE_MS + Math.random() * (FLIP_MAX_PAUSE_MS - FLIP_MIN_PAUSE_MS);
        }
        setWordRotation(i, rotation);
      });
      // Keep animating only while something is still mid-flip; otherwise drop back to the timer.
      if (anyFlipping(performance.now())) {
        rafRef.current = rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
        scheduleSleep();
      }
    }

    scheduleSleep();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (sleepTimer) clearTimeout(sleepTimer);
    };
  }, [reducedMotion, totalWords]);

  let wordIndex = -1;

  return (
    <>
      {wordsPerLine.map((words, lineIdx) => (
        <span key={lineIdx} className="block" style={{ perspective: "900px" }}>
          {words.map((word, i) => {
            const idx = ++wordIndex;
            return (
              <span
                key={i}
                ref={(el) => {
                  wordRefs.current[idx] = el;
                }}
                className="relative mr-[0.28em] inline-block will-change-transform"
                style={{
                  transformStyle: "preserve-3d",
                  transform: "rotateX(0deg)",
                  transformOrigin: "50% 50%",
                }}
              >
                {/* Two faces of the same word, pinned 180deg apart. Past 90deg from the camera a
                    rotateX'd element is seen from behind, which mirrors its content upside-down —
                    backfaceVisibility hides that face and the other one (already rotated to sit
                    exactly opposite) rotates into view right-reading instead. Both faces show the
                    same word, so which one is "front" at any moment never matters. */}
                <span className="invisible">{word}</span>
                <span className="absolute inset-0" style={{ backfaceVisibility: "hidden" }}>
                  {word}
                </span>
                <span
                  className="absolute inset-0"
                  style={{ backfaceVisibility: "hidden", transform: "rotateX(180deg)" }}
                >
                  {word}
                </span>
              </span>
            );
          })}
        </span>
      ))}
    </>
  );
}
